import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';

interface SwitchUIProps {
  checked?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
}

const SwitchLayout = styled.div<SwitchUIProps>`
  position: relative;
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${theme.borderRadius.xsmall}px;
  background-color: ${theme.color.neutral[50]};
  padding: ${toRem(4)};

  button:first-child {
    margin-right: ${toRem(4)};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${theme.color.neutral[200]};
    `}
`;

const Button = styled.button.attrs({ type: 'button' })<SwitchUIProps>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  flex-grow: 0;
  padding: ${toRem(7)} ${toRem(24)};
  border-radius: ${theme.borderRadius.xsmall}px;
  cursor: pointer;

  font-family: ${theme.font.family.euclid};
  color: ${theme.color.neutral[600]};
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(11)};
  line-height: 100%;
  text-align: center;
  transition: background-color ${theme.transition.button.fast};

  &:hover {
    background-color: ${theme.color.neutral[200]};
  }

  ${({ checked }) =>
    checked &&
    css`
      cursor: default;
      background-color: ${theme.color.secondary[400]};
      color: ${theme.color.white};

      &:hover {
        background-color: ${theme.color.secondary[300]};
      }
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;
    `}
`;

const LoadingSkeleton = styled(Skeleton)`
  && {
    height: ${toRem(22)};
    border-radius: ${toRem(theme.borderRadius.xsmall)};
  }
`;

interface SwitchProps extends SwitchUIProps {
  onChange: () => void;
  className?: string;
  dataCy?: string;
  leftText: string;
  rightText: string;
}

const Switch = ({ checked, leftText, rightText, disabled, isLoading, onChange, ...rest }: SwitchProps) => (
  <SwitchLayout disabled={disabled || isLoading} {...rest}>
    {isLoading ? (
      <LoadingSkeleton width={240} />
    ) : (
      <>
        <Button className={!checked ? 'checked' : ''} checked={!checked} onClick={onChange} disabled={disabled || !checked} data-cy="left-switch">
          {leftText}
        </Button>
        <Button className={checked ? 'checked' : ''} checked={checked} onClick={onChange} disabled={disabled || checked} data-cy="right-switch">
          {rightText}
        </Button>
      </>
    )}
  </SwitchLayout>
);

export default Switch;
